import type React from "react";
import {useRef, useState} from "react";

import {DragControls} from "@react-three/drei";
import * as THREE from "three";

import {useSelection} from "../../useSelection";

// Shared flag to track if any instance is being dragged
let isDraggingAnyInstance = false;

interface DraggableTransformProps {
  onTransform: (matrix: THREE.Matrix4) => void;
  onStart?: () => void;
  onEnd?: () => void;
  children: React.ReactNode;
}

export function DraggableTransform({
  onTransform,
  onStart,
  onEnd,
  children,
}: DraggableTransformProps) {
  const {dragging} = useSelection();
  const previousMatrixRef = useRef(new THREE.Matrix4());
  const [isActive, setIsActive] = useState(false); // Local active state

  const handleOnTransform = (matrix: THREE.Matrix4) => {
    dragging.current = true;
    onTransform(matrix);
  };

  return (
    <DragControls
      autoTransform={false}
      onDragStart={() => {
        if (isDraggingAnyInstance) {
          // If another instance is already dragging, cancel this drag
          return;
        }
        isDraggingAnyInstance = true; // Mark dragging as active globally
        setIsActive(true); // Mark this component as active
        onStart?.();
        previousMatrixRef.current.identity(); // Reset on drag start
      }}
      onDrag={(
        _localMatrix,
        deltaLocalMatrix,
        _worldMatrix,
        _deltaWorldMatrix,
      ) => {
        if (!isActive) return; // Only process transformations if this instance is active
        const deltaMatrix = new THREE.Matrix4()
          .copy(deltaLocalMatrix)
          .premultiply(previousMatrixRef.current.clone().invert());

        handleOnTransform(deltaMatrix); // Emit relative transformation matrix
        previousMatrixRef.current.copy(deltaLocalMatrix); // Update reference
      }}
      onDragEnd={() => {
        if (!isActive) return; // Ignore drag end if not active
        onEnd?.();
        previousMatrixRef.current.identity(); // Reset on drag end

        // Delay setting dragging.current to false
        setTimeout(() => {
          dragging.current = false;
        }, 0);

        setIsActive(false); // Deactivate this component
        isDraggingAnyInstance = false; // Clear global dragging flag
      }}
      dragConfig={{
        filterTaps: true,
      }}
    >
      {children}
    </DragControls>
  );
}
